import { SignalObject, signalObject } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { Plan, type UserInfo, type Preferences } from "@/api";
import { isEmail } from "@/utils";

import { createPreferencesSlice, type PreferencesSlice } from "./preferences";

type Slice = {
  info: UserInfo | undefined;
  isLoggedIn: boolean;
};

type Actions = {
  isPro: () => boolean;
  getUsersName: () => string | undefined;
  getNickname: () => string | undefined;
  setUserInfo: (userInfo: UserInfo) => void;
};

export type UserSlice = SignalObject<Slice> & { preferences: PreferencesSlice } & Actions;

export function createUserSlice(
  userBootstrap: UserInfo | undefined,
  preferencesBootstrap: Preferences | undefined,
): UserSlice {
  const initialAuthState = {
    isLoggedIn: !isEmpty(userBootstrap),
    info: isEmpty(userBootstrap) ? undefined : userBootstrap,
  };

  const preferenceSlice = createPreferencesSlice(initialAuthState.isLoggedIn ? preferencesBootstrap : undefined);
  const state = signalObject(initialAuthState);

  return {
    ...state,
    preferences: preferenceSlice,
    getUsersName: getUsersNameSelector(state),
    isPro: isProSelector(state),
    getNickname: getNicknameSelector(state),
    setUserInfo: setUserInfoFunc(state),
  };
}

function isProSelector(state: SignalObject<Slice>): () => boolean {
  /*
  User is considered pro if they are on a pending or pro plan.
  Pending means that they have paid but we have yet to retrieve/process the ack from stripe.
*/

  return () => {
    const plan = state.info.value?.plan || Plan.FREE;
    return plan === Plan.PENDING || plan === Plan.PRO;
  };
}

function getUsersNameSelector(state: SignalObject<Slice>): () => string | undefined {
  return () => {
    const names = state.info?.value?.name;

    if (names == null) return undefined;
    const possibleName = names.split(" ")[0];

    return isEmail(possibleName) ? undefined : possibleName;
  };
}

function getNicknameSelector(state: SignalObject<Slice>): () => string | undefined {
  return () => {
    return state.info?.value?.nickname;
  };
}

function setUserInfoFunc(state: SignalObject<Slice>): (userInfo: UserInfo) => void {
  return (userInfo: UserInfo) => {
    state.info.value = { ...state.info.value, ...userInfo };
  };
}
